import { useState } from 'react';
import SignInForm from '../components/auth/SignInForm';
import LoginHandler from './LoginHandler';
import { useAuth } from '../context/auth';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AuthApiError } from '@supabase/supabase-js';
import ConfirmEmail from '../components/auth/ConfirmEmail';
import { getAuthErrorTranslationKey } from '../utils/authErrors';

const Login = () => {
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { signIn, user, loading, resendConfirmationEmail } = useAuth();
  const { t } = useTranslation('auth');

  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [email, setEmail] = useState('');

  const handleResend = async () => {
    if (!email) return;
    try {
      await resendConfirmationEmail(email);
    } catch (error) {
      console.log('Error while sending.', error);
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      await signIn(email, password);
      setLoginSuccess(true);
    } catch (err: unknown) {
      let message: string | undefined = undefined;

      switch ((err as AuthApiError).code) {
        case 'invalid_credentials':
          message = t('login.invalid_credentials');
          break;

        case 'email_not_confirmed':
          setShowEmailConfirmation(true);
          setEmail(email);
          message = t('login.email_not_confirmed');

          try {
            await resendConfirmationEmail(email);
          } catch (resendError) {
            console.log('Unable to resend email :', resendError);
          }
          break;

        default:
          message = t(getAuthErrorTranslationKey(err, 'login.error'));
          break;
      }

      // The returned message is rendered inline by SignInForm, so only toast when
      // the form is replaced by the email confirmation screen (inline error hidden).
      if (message && (err as AuthApiError).code === 'email_not_confirmed') {
        toast.error(message);
      }
      return message;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (loginSuccess || user) {
    return <LoginHandler />;
  }

  if (showEmailConfirmation) {
    return <ConfirmEmail email={email} onResend={handleResend} />;
  }

  return <SignInForm onSubmit={handleSignIn} />;
};

export default Login;
